'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Home, Wrench, Search } from 'lucide-react';

const funnyMessages = [
  'Dieses Gerät konnten wir leider nicht reparieren. 🐰',
  'Seite defekt – aber kein Problem, wir kennen uns mit Defekten aus!',
  'Fehler 404: Seite auf Reparatur. Bitte warten.',
  'Hoppla! Unser Hase hat die Seite vernascht. 🥕',
  'Diese URL braucht dringend eine Reparatur.',
];

export default function NotFound() {
  const [msgIndex, setMsgIndex] = useState(0);
  const [wiggle, setWiggle] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex(prev => (prev + 1) % funnyMessages?.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  function handleLogoClick() {
    setWiggle(true);
    setTimeout(() => setWiggle(false), 600);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link href="/">
            <Image
              src="/assets/images/Media_Rabbit_1_Logo_Zugeschnitten-1782856074259.png"
              alt="Media Rabbit GmbH Logo"
              width={120}
              height={40}
              className="h-9 w-auto object-contain"
              priority
            />
          </Link>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full text-center">

          {/* Animated rabbit + 404 */}
          <div className="relative flex items-center justify-center mb-8 select-none">
            {/* Big 404 */}
            <span
              className="text-[9rem] sm:text-[11rem] font-extrabold leading-none"
              style={{
                background: 'linear-gradient(135deg, #e94560 0%, #ff6b35 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.04em',
              }}
            >
              404
            </span>

            {/* Rabbit mascot overlaid */}
            <button
              onClick={handleLogoClick}
              aria-label="Hase anklicken"
              className="absolute -right-4 sm:-right-8 bottom-2 focus:outline-none"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <div
                className={`transition-transform ${wiggle ? 'animate-bounce' : ''}`}
                style={{
                  fontSize: '4.5rem',
                  lineHeight: 1,
                  filter: 'drop-shadow(0 4px 12px rgba(233,69,96,0.25))',
                  display: 'inline-block',
                  transform: wiggle ? 'rotate(-15deg)' : 'rotate(8deg)',
                  transition: 'transform 0.3s cubic-bezier(.36,.07,.19,.97)',
                }}
              >
                🐰
              </div>
            </button>
          </div>

          {/* Rotating funny message */}
          <div className="min-h-[2.5rem] mb-3">
            <p
              key={msgIndex}
              className="text-base sm:text-lg font-semibold text-foreground"
              style={{
                animation: 'fadeSlideIn 0.5s cubic-bezier(0.22,1,0.36,1) both',
              }}
            >
              {funnyMessages?.[msgIndex]}
            </p>
          </div>

          <p className="text-muted-foreground text-sm mb-2">
            Die Seite, die du suchst, existiert nicht – oder wurde erfolgreich repariert und dabei versehentlich gelöscht.
          </p>

          {/* Fake "diagnosis" card */}
          <div
            className="card-surface mx-auto mt-6 mb-8 p-4 text-left max-w-sm"
            style={{ borderLeft: '3px solid #e94560' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Wrench size={14} className="text-primary flex-shrink-0" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">Diagnose-Bericht</span>
            </div>
            <div className="space-y-1.5 text-xs text-muted-foreground font-mono">
              <div className="flex justify-between">
                <span>Fehlercode:</span>
                <span className="text-foreground font-semibold">HTTP 404</span>
              </div>
              <div className="flex justify-between">
                <span>Ursache:</span>
                <span className="text-foreground font-semibold">Seite nicht gefunden</span>
              </div>
              <div className="flex justify-between">
                <span>Reparierbarkeit:</span>
                <span className="text-warning font-semibold">Unbekannt 🤷</span>
              </div>
              <div className="flex justify-between">
                <span>Empfehlung:</span>
                <span className="text-success font-semibold">Zurück zur Startseite</span>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/"
              className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 text-sm"
            >
              <Home size={15} />
              Zur Startseite
            </Link>
            <Link
              href="/reparatur-anfrage"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3 text-sm"
            >
              <Search size={15} />
              Reparatur anfragen
            </Link>
          </div>

          <p className="mt-8 text-xs text-muted-foreground">
            Noch Fragen?{' '}
            <a href="mailto:info@mediarabbit.de" className="text-primary hover:underline font-medium">
              info@mediarabbit.de
            </a>
          </p>
        </div>
      </main>
      {/* Footer hint */}
      <footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">
        © {new Date()?.getFullYear()} Media Rabbit GmbH · Hamburg ·{' '}
        <Link href="/impressum" className="hover:text-foreground transition-colors">
          Impressum
        </Link>
      </footer>
      <style jsx global>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}